import { HierarchyChildReadDto } from "functions/api/hierarchy/HierarchyChildReadDto";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { eHierarchyLevel } from "../../functions/api/enums";
import { HierarchyBackupVersionDto } from "../../functions/api/hierarchy/loadHierarchyVersions";
import { HierarchySearchDto } from "../../functions/api/hierarchy/searchAllHierarchies";

/**
 * Generates the base URL for a hierarchy based on its level and properties.
 *
 * The function constructs the ancestor-path format (e.g. `/CARDIO/D987/D9876C12345/CSR`) using the `taPrefix`, `projectCode`, and other hierarchy properties for each level.
 *
 * @param hierarchy - The hierarchy object to generate the URL for
 * @param pathName - Optional. The current path name to preserve navigation context
 * @returns The base URL for the hierarchy
 */
export const getHierarchyBaseUrl = (
  hierarchy:
    | HierarchyReadDto
    | HierarchySearchDto
    | HierarchyChildReadDto
    | HierarchyBackupVersionDto
    | {
        hierarchyTag: string;
        isBackupVersion: boolean;
        level: eHierarchyLevel;
        name: string;
        parentTag: string;
        tocMajorVersion?: number;
        taPrefix?: string;
        projectCode?: string;
      },
  pathName?: string,
): string => {
  /**
   * Joins non-empty string/number path segments into a slash-separated URL path.
   *
   * @param parts - Path segments to join; empty strings, booleans, and undefined values are omitted
   * @returns The joined path, always starting with a leading slash
   */
  const buildPath = (...parts: (string | number | boolean | undefined)[]): string =>
    "/" +
    parts
      .filter(
        (p): p is string | number => (typeof p === "string" && p !== "") || typeof p === "number",
      )
      .join("/");

  // Generate the base URL
  let baseUrl: string;
  switch (hierarchy.level) {
    case eHierarchyLevel.TA:
      baseUrl = buildPath(hierarchy.taPrefix);
      break;
    case eHierarchyLevel.Project:
      baseUrl = buildPath(hierarchy.taPrefix, hierarchy.projectCode);
      break;
    case eHierarchyLevel.Study:
      baseUrl = buildPath(hierarchy.taPrefix, hierarchy.projectCode, hierarchy.hierarchyTag);
      break;
    case eHierarchyLevel.Report: {
      const isBackup =
        "isBackupVersion" in hierarchy &&
        hierarchy.isBackupVersion === true &&
        hierarchy.tocMajorVersion !== undefined;
      // Full ancestor-path format.
      baseUrl = isBackup
        ? buildPath(
            hierarchy.taPrefix,
            hierarchy.projectCode,
            hierarchy.parentTag,
            hierarchy.name,
            "Version",
            hierarchy.tocMajorVersion,
          )
        : buildPath(hierarchy.taPrefix, hierarchy.projectCode, hierarchy.parentTag, hierarchy.name);

      break;
    }
    case (eHierarchyLevel.Corporate, eHierarchyLevel.TopLevel):
    default:
      baseUrl = "/Corporate";
  }

  // Navigate to the same path as needed.
  if (pathName) {
    const decodedPathName = decodeURIComponent(pathName);

    /* istanbul ignore else */
    if (decodedPathName.startsWith(baseUrl) && decodedPathName.length > baseUrl.length) {
      const remainingPath = decodedPathName.substring(baseUrl.length);
      return `${baseUrl}${remainingPath}`;
    }
  }
  return baseUrl;
};
